import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { ArticleItem } from '@/mocks/gossip';

interface ArticleCardProps {
  article: ArticleItem;
  variant?: 'standard' | 'row' | 'overlay';
}

const MetaLine = ({ article, light = false }: { article: ArticleItem; light?: boolean }) => (
  <div
    className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${
      light ? 'text-background-200/85' : 'text-foreground-500'
    }`}
  >
    <span className="flex items-center gap-1">
      <i className="ri-user-3-line text-sm" aria-hidden="true"></i>
      {article.author}
    </span>
    <span className="flex items-center gap-1">
      <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
      {article.date}
    </span>
    <span className="flex items-center gap-1">
      <i className="ri-fire-line text-sm" aria-hidden="true"></i>
      {article.views}
    </span>
  </div>
);

const Tag = ({ children }: { children: ReactNode }) => (
  <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900">
    {children}
  </span>
);

export default function ArticleCard({ article, variant = 'standard' }: ArticleCardProps) {
  if (variant === 'row') {
    return (
      <article className="group flex gap-4 border-b border-background-200 pb-4 last:border-b-0 last:pb-0">
        <Link
          to={`/article/${article.slug}`}
          className="relative h-24 w-32 shrink-0 overflow-hidden rounded-lg md:h-28 md:w-40"
        >
          <img
            src={article.image}
            alt={article.alt}
            title={`${article.title} - 诱惑吃瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div>
            <Link
              to={`/category/${article.category}`}
              className="text-[11px] font-semibold tracking-wider text-primary-600 transition-colors hover:text-primary-700"
            >
              {article.categoryName}
            </Link>
            <h3 className="mt-1.5 line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
              <Link to={`/article/${article.slug}`}>{article.title}</Link>
            </h3>
          </div>
          <p className="mt-2 text-xs text-foreground-500">{article.date} · {article.readTime}</p>
        </div>
      </article>
    );
  }

  if (variant === 'overlay') {
    return (
      <article className="group relative overflow-hidden rounded-lg">
        <Link to={`/article/${article.slug}`} className="block">
          <div className="relative h-72 w-full overflow-hidden md:h-96">
            <img
              src={article.image}
              alt={article.alt}
              title={`${article.title} - 诱惑吃瓜`}
              className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent" />
          </div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-semibold text-foreground-950">
              <i className="ri-star-smile-line text-sm" aria-hidden="true"></i>
              {article.categoryName}
            </span>
            <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
              {article.title}
            </h3>
            <p className="mt-2 line-clamp-2 max-w-2xl text-sm text-background-200/85">{article.excerpt}</p>
            <div className="mt-3">
              <MetaLine article={article} light />
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200">
      <Link to={`/article/${article.slug}`} className="relative block h-44 w-full overflow-hidden">
        <img
          src={article.image}
          alt={article.alt}
          title={`${article.title} - 诱惑吃瓜`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-[11px] font-semibold text-primary-700">
          {article.categoryName}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 font-heading text-lg font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          <Link to={`/article/${article.slug}`}>{article.title}</Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {article.tags.slice(0, 2).map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </div>
        <div className="mt-4 border-t border-background-200 pt-3">
          <MetaLine article={article} />
        </div>
      </div>
    </article>
  );
}